// @ts-nocheck
/**
 * THE CORE: POLYGLOT-FORGE — SOVEREIGN REGIONAL MATRIX (V1.0.0)
 * Automatic Country/Timezone/Language -> Currency (USD, GBP, EUR, SEK, NOK, DKK)
 * & Measurement System (Imperial sq ft / miles / acres vs. Metric m² / km / ha / tonnes)
 *
 * Designed for 0ms runtime execution in both React (Layer 1 t() + Layer 2 DOM Synchronizer,
 * including split JSX text nodes like <span>${price}</span>) and Vanilla JS / Static HTML engines.
 */

export const EU_COUNTRY_CODES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DE", "EE", "ES", "FI", "FR", "GR",
  "HU", "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PL", "PT", "RO", "SK", "SI",
  "CH", "IS", "LI", "MC", "SM", "VA", "AD"
]);

export const REGION_PROFILES = {
  US: {
    region: "US",
    currencyCode: "USD",
    currencySymbol: "$",
    symbolPosition: "prefix",
    rateFromUsd: 1.0,
    unitSystem: "imperial",
    areaUnit: "sq ft",
    areaUnitLabel: "SqFt",
    thousandSep: ",",
    decimalSep: ".",
  },
  GB: {
    region: "GB",
    currencyCode: "GBP",
    currencySymbol: "£",
    symbolPosition: "prefix",
    rateFromUsd: 0.79,
    unitSystem: "metric",
    areaUnit: "m²",
    areaUnitLabel: "m²",
    thousandSep: ",",
    decimalSep: ".",
  },
  EU: {
    region: "EU",
    currencyCode: "EUR",
    currencySymbol: "€",
    symbolPosition: "prefix",
    rateFromUsd: 0.92,
    unitSystem: "metric",
    areaUnit: "m²",
    areaUnitLabel: "m²",
    thousandSep: " ",
    decimalSep: ",",
  },
  FI: {
    region: "FI",
    currencyCode: "EUR",
    currencySymbol: "€",
    symbolPosition: "suffix",
    rateFromUsd: 0.92,
    unitSystem: "metric",
    areaUnit: "m²",
    areaUnitLabel: "m²",
    thousandSep: " ",
    decimalSep: ",",
  },
  SE: {
    region: "SE",
    currencyCode: "SEK",
    currencySymbol: "kr",
    symbolPosition: "suffix",
    rateFromUsd: 10.5,
    unitSystem: "metric",
    areaUnit: "m²",
    areaUnitLabel: "m²",
    thousandSep: " ",
    decimalSep: ",",
  },
  NO: {
    region: "NO",
    currencyCode: "NOK",
    currencySymbol: "kr",
    symbolPosition: "suffix",
    rateFromUsd: 10.8,
    unitSystem: "metric",
    areaUnit: "m²",
    areaUnitLabel: "m²",
    thousandSep: " ",
    decimalSep: ",",
  },
  DK: {
    region: "DK",
    currencyCode: "DKK",
    currencySymbol: "kr.",
    symbolPosition: "suffix",
    rateFromUsd: 6.9,
    unitSystem: "metric",
    areaUnit: "m²",
    areaUnitLabel: "m²",
    thousandSep: ".",
    decimalSep: ",",
  },
};

/**
 * Resolves the active regional profile based on selected language and detected Geo-IP country / timezone.
 * - Explicit Nordic language selections (sv, no, fi, da) always activate their corresponding Nordic regional profile.
 * - English (en) uses the visitor's detected country/timezone so UK visitors get £ GBP + m²,
 *   European visitors get € EUR + m², and US/International visitors get $ USD + sq ft.
 */
export function resolveRegionProfile(lang = "en", detectedCountry = "", timeZone = "") {
  if (lang === "sv") return REGION_PROFILES.SE;
  if (lang === "no") return REGION_PROFILES.NO;
  if (lang === "fi") return REGION_PROFILES.FI;
  if (lang === "da") return REGION_PROFILES.DK;

  const cc = String(detectedCountry || "").toUpperCase().trim();
  if (cc === "SE") return REGION_PROFILES.SE;
  if (cc === "NO") return REGION_PROFILES.NO;
  if (cc === "DK") return REGION_PROFILES.DK;
  if (cc === "FI") return REGION_PROFILES.FI;
  if (cc === "GB" || cc === "UK") return REGION_PROFILES.GB;
  if (EU_COUNTRY_CODES.has(cc)) return REGION_PROFILES.EU;
  if (cc === "US") return REGION_PROFILES.US;

  const tz = String(timeZone || "");
  if (tz.includes("London") || tz.includes("Belfast")) return REGION_PROFILES.GB;
  if (tz.includes("Stockholm")) return REGION_PROFILES.SE;
  if (tz.includes("Oslo")) return REGION_PROFILES.NO;
  if (tz.includes("Helsinki")) return REGION_PROFILES.FI;
  if (tz.includes("Copenhagen")) return REGION_PROFILES.DK;
  if (tz.startsWith("Europe/")) return REGION_PROFILES.EU;

  return REGION_PROFILES.US;
}

export function formatRegionalNumber(value, profile, decimals = 0) {
  const fixed = value.toFixed(decimals);
  const [intPart, decPart] = fixed.split(".");
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, profile.thousandSep);
  return decimals > 0 ? `${withThousands}${profile.decimalSep}${decPart}` : withThousands;
}

export function convertAndFormatCurrency(usdAmount, hadDecimals, decDigits, suffixMultiplier, profile) {
  let converted = usdAmount * profile.rateFromUsd;

  if (suffixMultiplier) {
    const dec = decDigits > 0 ? decDigits : converted < 10 ? 1 : 0;
    const numStr = formatRegionalNumber(converted, profile, dec);
    return profile.symbolPosition === "prefix"
      ? `${profile.currencySymbol}${numStr}${suffixMultiplier}`
      : `${numStr}${suffixMultiplier} ${profile.currencySymbol}`;
  }

  if (usdAmount > 0 && usdAmount < 1) {
    const sigDecimals = converted < 0.01 ? 4 : converted < 0.1 ? 3 : 2;
    const trimmed = Number(converted.toFixed(sigDecimals))
      .toString()
      .replace(".", profile.decimalSep);
    return profile.symbolPosition === "prefix"
      ? `${profile.currencySymbol}${trimmed}`
      : `${trimmed} ${profile.currencySymbol}`;
  }

  if (!hadDecimals) {
    if (usdAmount >= 1000) {
      converted = Math.round(converted / 50) * 50;
    } else if (usdAmount >= 100) {
      converted = Math.round(converted / 5) * 5;
    } else {
      converted = Math.round(converted);
    }
    const numStr = formatRegionalNumber(converted, profile, 0);
    return profile.symbolPosition === "prefix"
      ? `${profile.currencySymbol}${numStr}`
      : `${numStr} ${profile.currencySymbol}`;
  } else {
    const numStr = formatRegionalNumber(converted, profile, Math.min(decDigits, 2));
    return profile.symbolPosition === "prefix"
      ? `${profile.currencySymbol}${numStr}`
      : `${numStr} ${profile.currencySymbol}`;
  }
}

/**
 * Deterministic currency & measurement unit transformer for any string or text node.
 * Returns the original string untouched when profile.region === "US".
 */
export function localizeMetricsInString(input, profile) {
  if (!input || typeof input !== "string" || !profile || profile.region === "US") {
    return input;
  }

  let out = input;

  // 1. Compound rate: $XX / SqFt or $XX/sq ft -> local currency / m² (1 m² = 10.7639 sq ft)
  out = out.replace(
    /\$\s*([0-9][0-9,]*(?:\.[0-9]+)?)\s*(\/\s*)(?:SqFt|sq\.?\s*ft|sqft|SF)\b/gi,
    (_, numRaw, slash) => {
      const usdPerSqFt = parseFloat(numRaw.replace(/,/g, ""));
      if (isNaN(usdPerSqFt)) return _;
      const usdPerSqm = usdPerSqFt * 10.7639;
      const formattedPrice = convertAndFormatCurrency(usdPerSqm, false, 0, "", profile);
      return `${formattedPrice}${slash}${profile.areaUnit}`;
    }
  );

  // 2. Standard USD amounts: $5, $29/mo, $4,280,000, $0.002/call, $1.5M
  out = out.replace(
    /\$([0-9][0-9,]*(?:\.([0-9]+))?)([KMBkmb])?(?![0-9])/g,
    (fullMatch, numRaw, decPart, suffix) => {
      const cleanNum = parseFloat(numRaw.replace(/,/g, ""));
      if (isNaN(cleanNum)) return fullMatch;
      const hadDecimals = Boolean(decPart);
      const decDigits = decPart ? decPart.length : 0;
      return convertAndFormatCurrency(cleanNum, hadDecimals, decDigits, suffix || "", profile);
    }
  );

  // 3. Standalone "USD" code -> local currencyCode
  out = out.replace(/\bUSD\b/g, profile.currencyCode);

  // 4. Metric unit conversions (for GB, EU, FI, SE, NO, DK)
  if (profile.unitSystem === "metric") {
    // 4a. Numeric square feet: "10,000 sq ft", "25,000 SqFt", "5,000 sqft" -> "929 m²"
    out = out.replace(
      /\b([0-9][0-9,]*(?:\.[0-9]+)?)\s*(?:SqFt|sq\.?\s*ft|sqft)\b/gi,
      (fullMatch, numRaw) => {
        const sqft = parseFloat(numRaw.replace(/,/g, ""));
        if (isNaN(sqft)) return fullMatch;
        const sqm = Math.max(1, Math.round(sqft / 10.7639));
        return `${formatRegionalNumber(sqm, profile, 0)} ${profile.areaUnit}`;
      }
    );

    // 4b. Standalone SqFt / sq ft / sqft labels (e.g. "Price / SqFt", "Area (SqFt)")
    out = out.replace(/\b(?:SqFt|sq\.?\s*ft|sqft)\b/g, profile.areaUnitLabel);

    // 4c. Numeric miles -> km
    out = out.replace(/\b([0-9][0-9,]*(?:\.[0-9]+)?)\s*miles\b/gi, (fullMatch, numRaw) => {
      const mi = parseFloat(numRaw.replace(/,/g, ""));
      if (isNaN(mi)) return fullMatch;
      const km = Math.round(mi * 1.60934);
      return `${formatRegionalNumber(km, profile, 0)} km`;
    });

    // 4d. Numeric acres -> ha
    out = out.replace(/\b([0-9][0-9,]*(?:\.[0-9]+)?)\s*acres\b/gi, (fullMatch, numRaw) => {
      const ac = parseFloat(numRaw.replace(/,/g, ""));
      if (isNaN(ac)) return fullMatch;
      const ha = (ac * 0.404686).toFixed(1).replace(".", profile.decimalSep);
      return `${ha} ha`;
    });
  }

  return out;
}

/**
 * Handles React split JSX text node pairs such as `<span>${price}</span>`
 * where Node 1 ends with "$" and Node 2 (nextSibling) starts with a numeric value.
 */
export function localizeSplitCurrencyNodes(node, profile) {
  if (!node || !profile) return;
  const next = node.nextSibling;
  if (!next || next.nodeType !== 3) return;

  if (node.__splitOrigPrefix !== undefined && next.__splitOrigNum !== undefined) {
    if (profile.region === "US") {
      if (node.nodeValue !== node.__splitOrigPrefix) node.nodeValue = node.__splitOrigPrefix;
      if (next.nodeValue !== next.__splitOrigNum) next.nodeValue = next.__splitOrigNum;
      return;
    }
    const prefixWithoutDollar = node.__splitOrigPrefix.replace(/\$\s*$/, "");
    const convertedNum = localizeMetricsInString("$" + next.__splitOrigNum.trim(), profile);
    if (node.nodeValue !== prefixWithoutDollar) node.nodeValue = prefixWithoutDollar;
    if (next.nodeValue !== convertedNum) next.nodeValue = convertedNum;
    return;
  }

  const rawPrefix = node.nodeValue || "";
  const rawNext = next.nodeValue || "";
  if (/\$\s*$/.test(rawPrefix) && /^\s*[0-9][0-9,]*(?:\.[0-9]+)?/.test(rawNext)) {
    node.__splitOrigPrefix = rawPrefix;
    next.__splitOrigNum = rawNext;
    if (profile.region !== "US") {
      const prefixWithoutDollar = rawPrefix.replace(/\$\s*$/, "");
      const convertedNum = localizeMetricsInString("$" + rawNext.trim(), profile);
      node.nodeValue = prefixWithoutDollar;
      next.nodeValue = convertedNum;
    }
  }
}
